import { createReducer } from '@reduxjs/toolkit';
import get from 'lodash/get';
import reject from 'lodash/reject';
import sortBy from 'lodash/sortBy';

import replaceById from '../../utils/replace-by-id';
import { initializeBalance, updateLatestTask } from '../actions/actions';
import {
  addBookingEntry,
  applyDiscountToMatchBudget,
  deleteBookingEntry,
  generateBookingEntries,
  loadBookingEntries,
  updateBookingEntry,
} from '../actions/booking-entry-actions';
import {
  addInvoice,
  deleteInvoice,
  reloadInvoice,
  updateInvoice,
} from '../actions/invoice-actions';

/* eslint-disable no-param-reassign */

const updateLatestTaskInStore = (state, { payload: task }) => {
  state.context.latestTask = task;
};

const updatedFloatingBookingEntries = (bookingEntry, state) => {
  return replaceById(state.bookingEntries, bookingEntry);
};

const invoicesWithUpdatedBookingEntries = (bookingEntry, state) => {
  const invoiceThatHasUpdate = state.invoices.find(
    inv => inv.id === bookingEntry.invoice.id,
  );
  const invoiceBookingEntries = replaceById(
    invoiceThatHasUpdate.bookingEntries,
    bookingEntry,
  );

  return state.invoices.map(inv =>
    inv.id === bookingEntry.invoice.id
      ? { ...inv, bookingEntries: invoiceBookingEntries }
      : inv,
  );
};

export default createReducer(
  {
    invoices: [],
    bookingEntries: [],
    context: { opportunityId: 0, latestTask: null },
    loading: false,
  },
  {
    [`${initializeBalance}-pending`]: state => {
      return { ...state, loading: true };
    },

    [`${initializeBalance}-success`]: (state, { payload }) => {
      const bookingEntries =
        payload.bookingEntries &&
        payload.bookingEntries.map(resource => {
          return { ...resource };
        });
      return {
        bookingEntries: sortBy(bookingEntries, ['created', 'serviceStartDate']),
        invoices: sortBy(payload.invoices, ['created']),
        context: {
          opportunityId: get(payload, ['context', 'opportunityId'], 0),
          latestTask: get(payload, ['context', 'latestTask'], null),
        },
        loading: false,
      };
    },

    [`${initializeBalance}-error`]: state => {
      return { ...state, loading: false };
    },

    // booking entry reducers

    [`${loadBookingEntries}-success`]: (state, { payload: bookingEntries }) => {
      state.bookingEntries = bookingEntries;
    },

    [`${addBookingEntry}-success`]: (state, { payload: bookingEntry }) => ({
      ...state,
      bookingEntries: sortBy(state.bookingEntries.concat(bookingEntry), [
        'created',
        'serviceStartDate',
      ]),
    }),

    [`${updateBookingEntry}-success`]: (state, { payload: bookingEntry }) => {
      return bookingEntry.invoice
        ? {
            ...state,
            invoices: invoicesWithUpdatedBookingEntries(bookingEntry, state),
          }
        : {
            ...state,
            bookingEntries: updatedFloatingBookingEntries(bookingEntry, state),
          };
    },

    [`${deleteBookingEntry}-success`]: (state, { meta: { id } }) => ({
      ...state,
      bookingEntries: state.bookingEntries.filter(entry => entry.id !== id),
    }),

    [`${generateBookingEntries}-success`]: updateLatestTaskInStore,
    [`${updateLatestTask}`]: updateLatestTaskInStore,

    [`${applyDiscountToMatchBudget}-success`]: (state, { payload }) => {
      return {
        ...state,
        bookingEntries: updatedFloatingBookingEntries(payload, state),
      };
    },

    // invoice reducers

    [`${addInvoice}-success`]: (state, { payload: invoice }) => ({
      ...state,
      bookingEntries: reject(state.bookingEntries, bookingEntry =>
        invoice.bookingEntries.some(entry => entry.id === bookingEntry.id),
      ),
      invoices: state.invoices.concat(invoice),
    }),

    [`${updateInvoice}-success`]: (state, { payload: updatedInvoice }) => {
      const invoices = state.invoices.map(oldInvoice =>
        oldInvoice.id === updatedInvoice.id ? updatedInvoice : oldInvoice,
      );
      const oldInvoice = state.invoices.find(i => i.id === updatedInvoice.id);

      const oldBookingEntryIds = oldInvoice.bookingEntries.map(be => be.id);
      const newBookingEntryIds = updatedInvoice.bookingEntries.map(be => be.id);

      const addedIds = newBookingEntryIds.filter(
        id => !oldBookingEntryIds.includes(id),
      );
      const removedIds = oldBookingEntryIds.filter(
        id => !newBookingEntryIds.includes(id),
      );

      const bookingEntriesWithAdded = state.bookingEntries.filter(
        be => !addedIds.includes(be.id),
      );
      const bookingEntries = bookingEntriesWithAdded.concat(
        oldInvoice.bookingEntries.filter(be => removedIds.includes(be.id)),
      );

      return {
        ...state,
        invoices,
        bookingEntries,
      };
    },

    [`${deleteInvoice}-success`]: (state, { meta: { id } }) => {
      const deletedInvoice = state.invoices.find(invoice => invoice.id === id);
      return {
        ...state,
        invoices: state.invoices.filter(invoice => invoice.id !== id),
        bookingEntries: state.bookingEntries.concat(
          deletedInvoice.bookingEntries,
        ),
      };
    },

    [`${reloadInvoice}-success`]: (state, { payload: updatedInvoice }) => ({
      ...state,
      invoices: state.invoices.map(oldInvoice =>
        oldInvoice.id === updatedInvoice.id ? updatedInvoice : oldInvoice,
      ),
    }),
  },
  /* eslint-enable no-param-reassign */
);
